import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useOrgFilter } from "../scripts/OrgFilterContext";
import {
  consumePendingTour,
  getTourStatus,
  setTourStatus,
  clearTourStatus,
} from "@/lib/billingApi";
import { cn } from "@/lib/utils";

function buildSteps(orgId) {
  if (!orgId) return [];
  return [
    {
      id: "workspace-switcher",
      title: "Your workplaces",
      body: "Switch between organizations here, or create another workplace from the menu footer.",
      route: null,
    },
    {
      id: "nav-home",
      title: "Home & activity",
      body: "Dashboard is your catch-up and activity hub for the selected workplace.",
      route: "/dashboard",
    },
    {
      id: "org-teams",
      title: "Teams in this org",
      body: "This is your organization home — teams, members, and invites live here.",
      route: `/orgs/${encodeURIComponent(orgId)}`,
    },
    {
      id: "create-team",
      title: "Create your first team",
      body: "Teams hold channels, files, and optional boards. Use Create Team when you’re ready — we’ll open it from the last step.",
      route: `/orgs/${encodeURIComponent(orgId)}`,
    },
    {
      id: "nav-messages",
      title: "Messages",
      body: "Direct messages and channels for this workplace.",
      route: `/o/${encodeURIComponent(orgId)}/messages`,
    },
    {
      id: "notifications",
      title: "Notifications",
      body: "Mentions, invites, and reminders show up in the bell.",
      route: `/orgs/${encodeURIComponent(orgId)}`,
    },
    {
      id: "org-settings",
      title: "Settings & plan",
      body: "Admins manage branding, retention, Plan & usage, and more from Organization settings.",
      route: `/orgs/${encodeURIComponent(orgId)}/settings?section=plan`,
    },
    {
      id: "nav-profile",
      title: "Your profile",
      body: "Update your account and org contact details from the sidebar profile menu.",
      route: `/orgs/${encodeURIComponent(orgId)}`,
    },
    {
      id: "create-team",
      title: "Ready to build?",
      body: "Open the create-team wizard to add your first team, channels, and invites.",
      route: `/orgs/${encodeURIComponent(orgId)}`,
      openCreateTeam: true,
    },
  ];
}

/**
 * Lightweight spotlight tour. Anchors use [data-tour="…"] in the shell / org UI.
 */
export default function WorkspaceTour() {
  const { selectedOrgId } = useOrgFilter();
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [anchorRect, setAnchorRect] = useState(null);

  const orgId =
    selectedOrgId && selectedOrgId !== "social" ? selectedOrgId : null;
  const steps = buildSteps(orgId);
  const step = steps[stepIndex] || null;

  const finish = useCallback(
    (status) => {
      if (orgId) setTourStatus(orgId, status);
      setActive(false);
      setStepIndex(0);
      setAnchorRect(null);
    },
    [orgId]
  );

  const start = useCallback(
    (force) => {
      if (!orgId) return;
      if (!force && getTourStatus(orgId)) return;
      if (force) clearTourStatus(orgId);
      setStepIndex(0);
      setActive(true);
    },
    [orgId]
  );

  // Auto-start after create-org (pending flag) or when unset on first land
  useEffect(() => {
    if (!orgId) return;
    const pending = consumePendingTour();
    if (pending && String(pending) === String(orgId)) {
      start(true);
      return;
    }
    // Don't auto-start for existing orgs that never saw the tour unless pending
  }, [orgId, start]);

  useEffect(() => {
    const onStart = (e) => {
      const id = e.detail?.orgId;
      if (id && String(id) !== String(orgId)) return;
      start(true);
    };
    window.addEventListener("circa-start-workspace-tour", onStart);
    return () =>
      window.removeEventListener("circa-start-workspace-tour", onStart);
  }, [orgId, start]);

  // Navigate + measure anchor for current step
  useEffect(() => {
    if (!active || !step || !orgId) return;
    let cancelled = false;

    const run = async () => {
      if (step.route && location.pathname + location.search !== step.route) {
        const [path, qs] = step.route.split("?");
        navigate(qs ? `${path}?${qs}` : path);
        await new Promise((r) => setTimeout(r, 280));
      }
      if (cancelled) return;

      const measure = () => {
        const el = document.querySelector(`[data-tour="${step.id}"]`);
        if (el) {
          const r = el.getBoundingClientRect();
          setAnchorRect({
            top: r.top,
            left: r.left,
            width: r.width,
            height: r.height,
          });
          el.scrollIntoView({ block: "nearest", behavior: "smooth" });
        } else {
          setAnchorRect(null);
        }
      };
      measure();
      const t = window.setTimeout(measure, 320);
      return () => window.clearTimeout(t);
    };

    const cleanupPromise = run();
    return () => {
      cancelled = true;
      Promise.resolve(cleanupPromise).then((fn) => fn && fn());
    };
  }, [active, step, orgId, navigate, location.pathname, location.search, stepIndex]);

  const goNext = () => {
    if (!step) return;
    if (step.openCreateTeam) {
      window.dispatchEvent(
        new CustomEvent("circa-tour-open-create-team", {
          detail: { orgId },
        })
      );
      finish("done");
      return;
    }
    if (stepIndex >= steps.length - 1) {
      finish("done");
      return;
    }
    setStepIndex((i) => i + 1);
  };

  const goBack = () => {
    setStepIndex((i) => Math.max(0, i - 1));
  };

  if (!active || !step) return null;

  const pad = 6;
  const hole = anchorRect
    ? {
        top: Math.max(0, anchorRect.top - pad),
        left: Math.max(0, anchorRect.left - pad),
        width: anchorRect.width + pad * 2,
        height: anchorRect.height + pad * 2,
      }
    : null;

  const popTop = hole
    ? Math.min(
        window.innerHeight - 200,
        hole.top + hole.height + 12
      )
    : 80;
  const popLeft = hole
    ? Math.min(window.innerWidth - 340, Math.max(16, hole.left))
    : 24;

  return (
    <div className="fixed inset-0 z-[80]" role="dialog" aria-modal="true">
      {/* Transparent blocker so the page isn't clickable under the tour */}
      <div className="absolute inset-0" aria-hidden />
      {/* Spotlight only — no full-screen dim under the hole (that made the whole UI unreadable). */}
      {hole ? (
        <div
          className="pointer-events-none absolute rounded-md ring-2 ring-primary shadow-[0_0_0_9999px_rgba(15,23,42,0.28)]"
          style={{
            top: hole.top,
            left: hole.left,
            width: hole.width,
            height: hole.height,
            background: "transparent",
          }}
        />
      ) : (
        <div className="pointer-events-none absolute inset-0 bg-slate-900/30" />
      )}
      <div
        className={cn(
          "absolute z-[81] w-[min(100%-2rem,20rem)] rounded-lg border border-border bg-background p-4 shadow-lg"
        )}
        style={{ top: popTop, left: popLeft }}
      >
        <p className="text-xs text-muted-foreground mb-1">
          Step {stepIndex + 1} of {steps.length}
        </p>
        <h3 className="text-sm font-semibold">{step.title}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{step.body}</p>
        <div className="mt-4 flex flex-wrap gap-2 justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => finish("skipped")}
          >
            Skip tour
          </Button>
          {stepIndex > 0 && (
            <Button type="button" variant="outline" size="sm" onClick={goBack}>
              Back
            </Button>
          )}
          <Button type="button" size="sm" onClick={goNext}>
            {step.openCreateTeam
              ? "Create team"
              : stepIndex >= steps.length - 1
                ? "Done"
                : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
}
